import { Box, Card, CardActionArea, CardContent, Grid, Stack, Typography } from '@mui/material';
import DialogActions from '../../../Common/Dialog/DialogActions';
import DialogButton from '../../../Common/Buttons/DialogButton';
import DialogContent from '../../../Common/Dialog/DialogContent';
import DialogTitle from '../../../Common/Dialog/DialogTitle';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import getStoreFeatures from '../../../Common/_hooks/Store/getStoreFeatures';
import { STORE_ICON_URLS } from '../../StoreIconUrls';

interface Props {
	ready: boolean
	onSelectStore: (store: string) => void
	onDialogClose: () => void
}

export default function SelectStore(props: Props) {
	const [t] = useTranslation('account-v2')
	const userStores = useAppSelector(state => state.get('userData').get('stores'));
	const stores = useMemo(() => userStores
		.filter(store => getStoreFeatures(store.get('slug')).canCreateProduct), [userStores]);
	const storeIntegrations = useAppSelector(state => state.get('appData').get('storeIntegrations'));

	return <>
		<DialogTitle>{t('Select store')}</DialogTitle>
		<DialogContent>
			<Stack spacing={3}>
				<Stack spacing={1}>
					<Typography variant="h3">
						{t('Choose where to publish')}
					</Typography>
					<Typography variant="body1" color="text.secondary">
						{t('Select the connected store where this product should be published.')}
					</Typography>
					{!props.ready ? <Typography variant="body2" color="text.secondary">
						{t('Preparing product details...')}
					</Typography> : null}
				</Stack>

				<div>
					<Grid container spacing={2}>
						{stores.valueSeq().map((store) => {
							const slug = store.get('slug')
							const storeIntegration = storeIntegrations.get(slug)
							if (!storeIntegration) {
								return null
							}

							const integrationName = store.get('name') || storeIntegration.get('name')
							const storeName = store.get('store_name')?.trim() ?? ''
							const storeIconUrl = STORE_ICON_URLS[slug]

							return <Grid item xs={12} key={store.get('id')}>
								<Card variant="outlined" sx={{ height: '100%' }}>
									<CardActionArea
										disabled={!props.ready}
										onClick={() => props.onSelectStore(slug)}
										sx={{ height: '100%' }}
									>
										<CardContent
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 2,
												p: 2.5,
												'&:last-child': {
													pb: 2.5,
												},
											}}
										>
											<Box
												component="img"
												src={storeIconUrl}
												alt={t('Logo of {{store}}', { store: integrationName })}
												sx={{
													width: 48,
													height: 48,
													flexShrink: 0,
													objectFit: 'contain',
												}}
											/>
											<Stack spacing={0.25} sx={{ minWidth: 0 }}>
												<Typography variant="h4">{integrationName}</Typography>
												{storeName !== '' ? <Typography variant="body2" color="text.secondary" noWrap>
													{storeName}
												</Typography> : null}
											</Stack>
										</CardContent>
									</CardActionArea>
								</Card>
							</Grid>
						})}
					</Grid>
				</div>
			</Stack>
		</DialogContent>

		<DialogActions>
			<DialogButton
				color="secondary"
				onClick={props.onDialogClose}
			>{t('Cancel')}</DialogButton>
		</DialogActions>
	</>
}
